import { useState, useEffect } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, MapPin, Star, ArrowLeft, ArrowRight, Loader2, User } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useFlow } from "../context/FlowContext";
import { API } from "../api";
import { PremiumIcon } from "../components/PremiumIcon";

const photoUrl = (path) => `${API}/public/files/${path}`;

const MiniHeader = () => (
  <header className="border-b border-line bg-white/90 backdrop-blur sticky top-0 z-40">
    <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
      <Link to="/" className="font-heading font-extrabold text-lg tracking-tight text-ink flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full bg-forest inline-block" /> HomeHealthy
      </Link>
      <Link to="/profesionales" className="text-sm font-medium text-ink-soft hover:text-ink">Profesionales</Link>
    </div>
  </header>
);

const Chip = ({ category, label }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-line bg-cream px-3 py-1.5 text-sm text-ink">
    <PremiumIcon category={category} size={16} hoverGroup={false} className="!w-7 !h-7 !rounded-lg" /> {label}
  </span>
);

export const ProfessionalProfile = () => {
  const { t } = useLang();
  const p = t.publicPro;
  const { openClient } = useFlow();
  const { id } = useParams();
  const [pro, setPro] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    axios.get(`${API}/professionals/public/${id}`)
      .then(({ data }) => { setPro(data); setState("ok"); })
      .catch(() => setState("notfound"));
  }, [id]);

  if (state === "loading") return <div className="min-h-screen flex items-center justify-center bg-cream"><Loader2 className="animate-spin text-forest" /></div>;
  if (state === "notfound") return (
    <div className="min-h-screen bg-cream">
      <MiniHeader />
      <div className="max-w-3xl mx-auto px-6 py-32 text-center text-ink-soft">{p.notFound}
        <div className="mt-6"><Link to="/profesionales" className="text-forest font-semibold underline">{p.back}</Link></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-cream">
      <MiniHeader />
      <div className="max-w-5xl mx-auto px-6 py-10 md:py-16">
        <Link to="/profesionales" className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-ink mb-6" data-testid="profile-back"><ArrowLeft size={15} /> {p.back}</Link>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="grid md:grid-cols-5 gap-8 items-start">
          <div className="md:col-span-2">
            <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] bg-cream-deep shadow-[0_24px_50px_rgb(3,33,42,0.12)]">
              {pro.photo ? (
                <img src={photoUrl(pro.photo)} alt={pro.name} className="w-full h-full object-cover" data-testid="profile-photo" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-ink-faint"><User size={64} /></div>
              )}
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="inline-flex items-center gap-2 rounded-full bg-forest/10 text-forest px-3 py-1.5 text-sm font-semibold" data-testid="profile-verified">
              <BadgeCheck size={16} /> {p.verified}
            </div>
            <h1 className="mt-4 font-heading text-4xl md:text-5xl font-light tracking-tight text-ink">{pro.name} {pro.last_name}</h1>
            <p className="mt-2 text-sm text-ink-faint">{p.verifiedNote}</p>

            <div className="mt-5 flex items-center gap-2 text-ink-soft"><MapPin size={18} className="text-forest" /> <span>{p.serviceArea}: {pro.city}{pro.postal_code ? ` · ${pro.postal_code}` : ""}</span></div>

            {(pro.rating || pro.services_done) && (
              <div className="mt-5 flex flex-wrap gap-6">
                {pro.rating && (
                  <div className="flex items-center gap-2" data-testid="profile-rating">
                    <div className="flex">{[1,2,3,4,5].map((n) => <Star key={n} size={18} className={n <= Math.round(pro.rating) ? "fill-olive text-olive" : "text-line"} />)}</div>
                    <span className="text-ink font-semibold">{pro.rating}</span>
                    <span className="text-ink-faint text-sm">({pro.reviews_count})</span>
                  </div>
                )}
                {pro.services_done && <div className="text-ink-soft"><span className="font-semibold text-ink">{pro.services_done}</span> {p.servicesDone}</div>}
              </div>
            )}

            <div className="mt-7">
              <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-forest">{p.services}</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {pro.categories.map((c) => <Chip key={c} category={c} label={t.categories[c]?.label || c} />)}
              </div>
            </div>

            {(pro.experience || pro.languages) && (
              <div className="mt-7 grid sm:grid-cols-2 gap-6">
                {pro.experience && <div><h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-forest">{p.experience}</h3><p className="mt-2 text-ink">{pro.experience}</p></div>}
                {pro.languages && <div><h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-forest">{p.languages}</h3><p className="mt-2 text-ink">{pro.languages}</p></div>}
              </div>
            )}

            {pro.description && <div className="mt-7"><h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-forest">{p.about}</h3><p className="mt-2 text-ink-soft leading-relaxed">{pro.description}</p></div>}

            {pro.reviews && pro.reviews.length > 0 && (
              <div className="mt-8" data-testid="profile-reviews">
                <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-forest">{p.reviews}</h3>
                <div className="mt-3 space-y-3">
                  {pro.reviews.map((r, i) => (
                    <div key={i} className="rounded-2xl border border-line bg-white p-4">
                      <div className="flex">{[1,2,3,4,5].map((n) => <Star key={n} size={14} className={n <= r.rating ? "fill-olive text-olive" : "text-line"} />)}</div>
                      <p className="mt-2 text-ink-soft">{r.comment}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button onClick={() => openClient()} className="group mt-9 inline-flex items-center gap-2 rounded-full bg-forest text-white font-semibold px-7 py-4 hover:bg-sage-deep transition-all duration-300 hover:-translate-y-1" data-testid="profile-cta">
              {p.cta} <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export const ProfessionalsDirectory = () => {
  const { t } = useLang();
  const p = t.publicPro;
  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/professionals/public`).then(({ data }) => setPros(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-cream">
      <MiniHeader />
      <div className="max-w-6xl mx-auto px-6 py-12 md:py-20">
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-forest">HomeHealthy</span>
        <h1 className="mt-3 font-heading text-4xl md:text-5xl font-light tracking-tight text-ink">{p.directoryTitle}</h1>
        <p className="mt-3 text-lg text-ink-soft max-w-2xl">{p.directorySubtitle}</p>

        {loading ? (
          <div className="py-24 text-center"><Loader2 className="animate-spin text-forest inline" /></div>
        ) : pros.length === 0 ? (
          <p className="py-24 text-center text-ink-faint">{p.empty}</p>
        ) : (
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {pros.map((pro) => (
              <Link key={pro.id} to={`/profesional/${pro.id}`} className="group bg-white rounded-3xl border border-line overflow-hidden hover:shadow-[0_24px_50px_rgb(3,33,42,0.12)] transition-all duration-500 hover:-translate-y-1" data-testid={`dir-pro-${pro.id}`}>
                <div className="aspect-[4/3] bg-cream-deep overflow-hidden">
                  {pro.photo ? <img src={photoUrl(pro.photo)} alt={pro.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /> : <div className="w-full h-full flex items-center justify-center text-ink-faint"><User size={48} /></div>}
                </div>
                <div className="p-6">
                  <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-forest"><BadgeCheck size={14} /> {p.verified}</div>
                  <h3 className="mt-2 font-heading text-xl font-medium text-ink">{pro.name} {pro.last_name}</h3>
                  <p className="text-sm text-ink-faint">{pro.city}{pro.postal_code ? ` · ${pro.postal_code}` : ""}</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {pro.categories.map((c) => <span key={c} className="rounded-full bg-cream px-2.5 py-1 text-xs text-ink-soft">{t.categories[c]?.label || c}</span>)}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
